"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import TransitionLink from "@/components/common/TransitionLink";
import styles from "./MobileMenu.module.scss";
import Link from "next/link";
import { contra, instagram, linkedin, twitter } from "@/utils/routes";

interface MobileMenuProps {
	isOpen: boolean;
	onClose: () => void;
}

const menuItems = [
	{ label: "Home", href: "/" },
	{ label: "Work", href: "/projects" },
	{ label: "The Studio", href: "/the-studio" },
	{ label: "Contact", href: "/contact" },
];

const socialLinks = [
	{ label: "LinkedIn", href: linkedin },
	{ label: "Contra", href: contra },
	{ label: "Twitter", href: twitter },
	{ label: "Instagram", href: instagram },
];

const MobileMenu = ({ isOpen, onClose }: MobileMenuProps) => {
	const menuRef = useRef<HTMLDivElement>(null);
	const menuItemsRef = useRef<(HTMLLIElement | null)[]>([]);
	const linesRef = useRef<(HTMLDivElement | null)[]>([]);
	const socialsRef = useRef<HTMLDivElement>(null);
	const emailRef = useRef<HTMLDivElement>(null);
	const lgRef = useRef<HTMLDivElement>(null);
	const pl25Ref = useRef<HTMLDivElement>(null);
	const closeRef = useRef<HTMLButtonElement>(null);
	const timelineRef = useRef<gsap.core.Timeline | null>(null);

	useEffect(() => {
		// Prevent body scroll when menu is open
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}

		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	useEffect(() => {
		if (!menuRef.current) return;

		// Create the timeline only once if it doesn't exist
		if (!timelineRef.current) {
			const tl = gsap.timeline({ paused: true });
			timelineRef.current = tl;

			// Set initial states
			gsap.set(menuItemsRef.current, { opacity: 0, y: 30 });
			gsap.set(linesRef.current, { scaleX: 0, transformOrigin: "left center" });
			gsap.set([socialsRef.current, emailRef.current, lgRef.current, pl25Ref.current, closeRef.current], {
				opacity: 0,
				y: 20,
			});

			// Animate menu container
			tl.to(menuRef.current, {
				opacity: 1,
				duration: 0.4,
				ease: "power2.out",
			});

			// Animate close button
			tl.to(
				closeRef.current,
				{
					opacity: 1,
					y: 0,
					duration: 0.5,
					ease: "power2.out",
				},
				"-=0.2"
			);

			// Animate menu items with stagger
			tl.to(
				menuItemsRef.current,
				{
					opacity: 1,
					y: 0,
					duration: 0.6,
					stagger: 0.08,
					ease: "power2.out",
				},
				"-=0.3"
			);

			// Animate lines from left to right
			tl.to(
				linesRef.current,
				{
					scaleX: 1,
					duration: 0.8,
					stagger: 0.08,
					ease: "power2.inOut",
				},
				"-=0.5"
			);

			// Animate footer elements
			tl.to(
				[lgRef.current, socialsRef.current, emailRef.current],
				{
					opacity: 1,
					y: 0,
					duration: 0.5,
					stagger: 0.1,
					ease: "power2.out",
				},
				"-=0.4"
			);

			// Animate PL'25
			tl.to(
				pl25Ref.current,
				{
					opacity: 1,
					y: 0,
					duration: 0.6,
					ease: "power2.out",
				},
				"-=0.3"
			);
		}

		// Play forward or reverse based on isOpen state
		if (isOpen) {
			timelineRef.current.play();
		} else {
			timelineRef.current.reverse();
		}

		// Cleanup function to kill timeline on unmount
		return () => {
			if (timelineRef.current) {
				timelineRef.current.kill();
				timelineRef.current = null;
			}
		};
	}, [isOpen]);

	return (
		<div ref={menuRef} className={`${styles.mobileMenu} ${isOpen ? styles.open : ""}`}>
			<div className={styles.mobileMenu__header}>
				<img src="/images/logo.svg" alt="Parallel Lab" width={44.03} height={40} data-no-placeholder />
				<button ref={closeRef} className={styles.mobileMenu__close} onClick={onClose}>
					CLOSE
				</button>
			</div>

			<nav className={styles.mobileMenu__nav}>
				<svg width="24" height="21" viewBox="0 0 24 21" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M13.2045 20.3638L11.4545 18.6365L18.6591 11.432H0V8.93195H18.6591L11.4545 1.75013L13.2045 0.000133514L23.3864 10.182L13.2045 20.3638Z" fill="white" style={{ fill: "white", fillOpacity: 1 }} />
				</svg>

				<ul className={styles.mobileMenu__list}>
					{menuItems.map((item, index) => (
						<li
							key={item.label}
							ref={(el) => {
								menuItemsRef.current[index] = el;
							}}
							className={styles.mobileMenu__item}
						>
							<Link href={item.href} onClick={onClose}>
								<span>{item.label}</span>
							</Link>
							<div
								ref={(el) => {
									linesRef.current[index] = el;
								}}
								className={styles.mobileMenu__line}
							/>
						</li>
					))}
				</ul>
			</nav>

			<div className={styles.mobileMenu__footer}>
				<div ref={lgRef} className={styles.mobileMenu__lg}>
					LG_NG
				</div>

				<div className={styles.mobileMenu__right}>
					<div ref={socialsRef} className={styles.mobileMenu__socials}>
						{socialLinks.map((social) => (
							<a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer">
								{social.label}
							</a>
						))}
					</div>

					<div ref={emailRef} className={styles.mobileMenu__email}>
						<p>Keen to work with us?</p>
						<p>Drop us a mail</p>
						<a href="mailto:theparallellab@gmail.com">theparallellab@gmail.com</a>
					</div>
				</div>
			</div>

			<div ref={pl25Ref} className={styles.mobileMenu__pl25}>
				<img src="/images/pl-25-white.svg" alt="PL'25" width={335} height={102} data-no-placeholder />
			</div>
		</div>
	);
};

export default MobileMenu;
